/* eslint-disable eqeqeq */
import React from 'react';

import { FormattedMoney } from '@meterup/atto';
import { Quote, QuoteRequest } from '@meterup/connect-api';
import { FormattedQuoteMonthlyAmountSimple } from '@meterup/connect-ui/src/FormattedMonthlyAmount/FormattedMonthlyAmount';

import { formatSelectionPriority } from '../../../utils/formatSelectionPriority';

interface QuotesContractSidebarProps {
  quoteRequest: QuoteRequest;
  selectedQuotes: Quote[];
}

function QuotesContractSidebar({ quoteRequest, selectedQuotes }: QuotesContractSidebarProps) {
  const totalInstallFeeCents = selectedQuotes.reduce((sum, q) => sum + q.installFeeCents, 0);
  const totalMonthlyFeeCents = selectedQuotes.reduce((sum, q) => sum + q.monthlyFeeCents, 0);

  const renderQuoteWithDetails = (quote: Quote) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-bold text-gray-800">{quote?.provider?.name}</span>
          <span className="text-xs text-gray-600">{quote?.connectionType?.name}</span>
        </div>
        <span className="font-medium text-xs bg-gray-100 rounded p-1">
          {formatSelectionPriority(selectedQuotes.findIndex((q) => q.sid === quote.sid) + 1)}{' '}
          connection
        </span>
      </div>
      <div className="flex justify-between mt-2 mb-2 align-center items-center text-gray-600 text-sm">
        <span className="font-normal">Install fee</span>
        <p className="text-sm font-normal">
          <span className="mr-1">one-time</span>
          <span className="text-gray-800">
            <FormattedMoney amount={Math.ceil(quote.installFeeCents / 100)} />
          </span>
        </p>
      </div>
      <div className="flex justify-between mt-2 align-center items-center text-gray-600 text-sm">
        <span className="font-normal">
          {quote.contractLengthMonths === 1
            ? 'Month-to-month contract'
            : `${quote.contractLengthMonths}-month contract`}
        </span>
        <p className="text-sm font-normal">
          <span className="mr-2">monthly</span>
          <span className="text-gray-800">
            <FormattedQuoteMonthlyAmountSimple quote={quote} />
          </span>
        </p>
      </div>
    </div>
  );

  const renderQuotes = () => (
    <>
      {selectedQuotes.map((q) => (
        <div key={q.sid} className="mb-4 pb-4 border-b border-gray-100">
          {renderQuoteWithDetails(q)}
        </div>
      ))}
    </>
  );

  return (
    <section>
      {renderQuotes()}
      {quoteRequest.isInterestedInNetwork && (
        <div className="mb-4 pb-4 border-b border-gray-100 text-gray-600">
          <p className="text-gray-800 mr-4">Meter Network</p>
          <p className="font-normal text-sm">
            Get connected to fast, secure, and reliable internet & WiFi. We’ll contact you with a
            quote.
          </p>
        </div>
      )}
      <div className="flex justify-between mt-2 mb-2 align-center items-center text-gray-800">
        <span className="text-sm font-normal">Total install fee</span>
        <p className="text-sm font-normal">
          <span className="mr-2">one-time</span>
          <span className="text-gray-800">
            <FormattedMoney amount={Math.ceil(totalInstallFeeCents / 100)} />
          </span>
        </p>
      </div>
      <div className="flex justify-between align-center items-center text-gray-800">
        <span className="text-sm font-normal">Total monthly rate</span>
        <span className="text-sm font-normal">
          <span className="text-lg font-bold text-gray-800">
            <FormattedMoney amount={Math.ceil(totalMonthlyFeeCents / 100)} />
          </span>
        </span>
      </div>
    </section>
  );
}

export default QuotesContractSidebar;
